import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Textarea } from '../../../../components/inputs/Textarea';
import { Modal } from '../../../../components/Modal';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { mySurveysApi } from '../mySurveysApi';

const DECISION_ID = 'my-survey-recommendation-decision';

/**
 * The employee's answer to a sent recommendation — FINAL, once. Accept needs only the tick;
 * Decline needs a reason, which is what Learning & Development is told. NO Autogenerate: whether
 * to take a training, and why not, is the employee's own answer.
 */
export function MySurveyRecommendationDecisionModal({
  surveyId,
  recommendationId,
  title,
  accept,
  onClose,
  onDecided,
}: {
  surveyId: string;
  recommendationId: string;
  title: string;
  accept: boolean;
  onClose: () => void;
  onDecided: () => void;
}) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const canSave = confirmed && !saving && (accept || reason.trim().length > 0);

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      if (accept) {
        await mySurveysApi.acceptRecommendation(surveyId, recommendationId);
        toast.success(`You accepted "${title}".`);
      } else {
        await mySurveysApi.declineRecommendation(surveyId, recommendationId, reason.trim());
        toast.success(`You declined "${title}". Learning & Development will see your reason.`);
      }

      onDecided();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Your answer could not be recorded.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: DECISION_ID });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={accept ? 'Accept training recommendation' : 'Decline training recommendation'}
      size="lg"
      id={`${DECISION_ID}-modal`}
      confirm={{ id: `${DECISION_ID}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id={`${DECISION_ID}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id={`${DECISION_ID}-submit`}
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/* THE CONSEQUENCE BEFORE THE TICK, and it differs per answer. */}
        <p className="text-sm text-slate-600">
          {accept
            ? `Accepting "${title}" is final. You can then record when the training starts and ends.`
            : `Declining "${title}" is final. Learning & Development is told your reason.`}
        </p>

        {accept ? null : (
          <Textarea
            id={`${DECISION_ID}-reason`}
            label="Reason"
            required
            maxLength={2000}
            description="Why you are declining this training. Learning & Development reads this."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            error={reason.trim() ? error?.fieldError('reason') : 'Say why you are declining.'}
            disabled={saving}
          />
        )}
      </div>
    </Modal>
  );
}
